import React, {useContext, useEffect, useState} from "react";
import * as Sentry from '@sentry/browser';
import {FormattedMessage} from "react-intl";
import Api from "../../Api";
import {withAuth} from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import {useLocation} from "react-router-dom";

import EmptyCard from "../../hoc/EmptyCard";
import RegistryTable from "../../Components/RegistryTable";
import RegistryControls from "../../Components/RegistryControls";
import RegistryUploadInfo from "../../Components/RegistryUploadInfo"
import RegistryDADUpload from "../../Components/RegistryDADUpload";
import RegistryColumnsHelp from "../../Components/RegistryColumnsHelp";
import RegistryInfoCard, {NoTerminalsForMethod, WrongRegistryTypeHint} from "../../Components/RegistryInfoCard";
import {filterTerminalsByMethod, PAYOUT_METHOD_CARD} from "../../Services/payoutMethods";
import {Select} from "../../Components/Utils/FieldsHelper";
import Modal from "../../Components/Utils/Modal";
import Icon from "../../Components/Utils/Icon";

import classes from "./styles.module.css"

import Context from "../../Context";
import MessageContext from "../../MessageContext";

const RegistryUpload = withAuth(({token}) => {

        const location = useLocation();

        const [projectUUID, setProjectUUID] = useState(null);
        const [providerUUID, setProviderUUID] = useState(null);

        const [selectedProject, setSelectedProject] = useState(null);
        const [selectedTerminal, setSelectedTerminal] = useState(null);

        const [initRegistry, setInitRegistry] = useState([])
        const [filteredRegistry, setFilteredRegistry] = useState([])
        const [initRegistryName, setInitRegistryName] = useState('')
        const [registryName, setRegistryName] = useState('')
        const [registryAmount, setRegistryAmount] = useState(0);

        // Флаг предзагруженного реестра
        const [, setIsPreloaded] = useState(false);

        const [projects, setProjects] = useState([]);
        const [terminals, setTerminals] = useState([]);

        const [commission, setCommission] = useState(0);

        const [errorsCount, setErrorsCount] = useState(0);
        const [errorsList, setErrorsList] = useState([]);
        const [errorsFormat, setErrorsFormat] = useState(false);
        // «Похоже, это реестр СБП» — подсказка приезжает с бэкенда вместе с
        // ошибкой формата: разбор шапки файла живёт там же, где загрузчик.
        const [wrongRegistryType, setWrongRegistryType] = useState(null);
        const [wrongRegistryHint, setWrongRegistryHint] = useState(null);
        const [tags, setTags] = useState([]);
        const [suggestionsTags, setSuggestionsTags] = useState([]);
        const [terminalBalance, setTerminalBalance] = useState({balance: 0, currency: "RUB"});

        const [loadingRegistry, setLoadingRegistry] = useState(false);
        const [loadingProjects, setLoadingProjects] = useState(true);
        const [loadingTerminals, setLoadingTerminals] = useState(false);

        const [isRequest, setIsRequest] = useState(false);

        // Duplicate detection states
        const [duplicates, setDuplicates] = useState([]);
        const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
        const [pendingFile, setPendingFile] = useState(null);
        const [pendingData, setPendingData] = useState(null);

        const {onMessage} = useContext(MessageContext);

        useEffect(() => {
            setLoadingProjects(true);

            new Api({token})
                .getProjects()
                .then((r) => {
                    setProjects(Array.isArray(r) ? r : []);
                })
                .catch((err) => {
                    Sentry.captureException(err);
                    setProjects([]);
                    onMessage({type: "error", message: "Не удалось загрузить список проектов."});
                })
                .finally(() => {
                    setLoadingProjects(false);
                });
        }, [token]);

        // Обработка предзагруженного реестра из PayoutsDetail
        useEffect(() => {
            if (location.state && location.state.preloadedRegistry) {
                const preloadedData = location.state.preloadedRegistry;
                const preloadedName = location.state.preloadedName || 'Повтор реестра';

                if (Array.isArray(preloadedData) && preloadedData.length > 0) {
                    // Рассчитываем общую сумму
                    let amount = 0;
                    preloadedData.forEach(function (el) {
                        var v = el && el.row ? el.row.amount : 0;
                        amount += Number(v) || 0;
                    });

                    setInitRegistry(preloadedData);
                    setFilteredRegistry(preloadedData);
                    setInitRegistryName(preloadedName);
                    setRegistryName(preloadedName);
                    setRegistryAmount(amount);
                    setLoadingRegistry(true);
                    setIsPreloaded(true);

                    onMessage({
                        type: "info",
                        message: `Загружен реестр "${preloadedName}" с ${preloadedData.length} записями для повторной отправки.`
                    });
                }

                // Очищаем state чтобы при обновлении страницы не загружался снова
                window.history.replaceState({}, document.title);
            }
        }, [location.state]);

        const processing = selectedTerminal ? selectedTerminal.processing : null;

        useEffect(() => {
            if (!selectedTerminal || !Array.isArray(initRegistry) || initRegistry.length === 0) return;

            const next = applyFinStarDocValidation(initRegistry, processing);
            setInitRegistry(next);

            if (Array.isArray(filteredRegistry) && filteredRegistry.length > 0) {
                setFilteredRegistry(applyFinStarDocValidation(filteredRegistry, processing));
            }
        }, [processing]);

        function calcCommission(rows, payoutFee) {
            // rows: массив строк реестра (data.data)
            if (!Array.isArray(rows) || rows.length === 0) {
                setCommission(0);
                return;
            }

            // payoutFee: объект terminal.fee.payout
            if (!payoutFee || typeof payoutFee !== "object") {
                setCommission(0);
                return;
            }

            var cost = Number(payoutFee.cost != null ? payoutFee.cost : 0); // %
            var min = Number(payoutFee.costFixedMinimum != null ? payoutFee.costFixedMinimum : 0);
            var fixed = Number(payoutFee.fixedPerTransaction != null ? payoutFee.fixedPerTransaction : 0);

            var total = 0;

            rows.forEach(function (el) {
                var amountRaw = el && el.row ? el.row.amount : 0;
                var a = Number(amountRaw);

                if (!isFinite(a) || a <= 0) return;

                var pct = a * (cost / 100);
                total += (pct < min ? min : pct) + fixed;
            });

            setCommission(total);
        }

        function isEmptyValue(v) {
            return v === null || v === undefined || String(v).trim() === "";
        }

        function addFieldError(errors, fieldKey, message) {
            if (!errors) {
                return {[fieldKey]: message};
            }

            // Если уже объект — пишем по ключу поля (лучше для подсветки ячейки)
            if (!Array.isArray(errors) && typeof errors === "object") {
                if (!errors[fieldKey]) {
                    return {...errors, [fieldKey]: message};
                }
                return errors;
            }

            // Если массив — добавляем строку (не ломаем существующую логику)
            if (Array.isArray(errors)) {
                if (errors.includes(message)) return errors;
                return [...errors, message];
            }

            // fallback
            return {[fieldKey]: message};
        }

        function applyFinStarDocValidation(rows, processing) {
            const isFinStar = String(processing || "").toLowerCase() === "finstar";
            if (!isFinStar || !Array.isArray(rows)) return rows;

            return rows.map((el) => {
                const row = el && el.row ? el.row : {};
                let hasError = Boolean(el && el.hasError);
                let errors = el ? el.errors : undefined;

                if (isEmptyValue(row.documentNumber)) {
                    hasError = true;
                    errors = addFieldError(errors, "documentNumber", "Номер документа обязателен для FinStar.");
                }
                if (isEmptyValue(row.documentDate)) {
                    hasError = true;
                    errors = addFieldError(errors, "documentDate", "Дата документа обязательна для FinStar.");
                }

                return {...el, row, hasError, errors};
            });
        }

        function onChangeRegistryName(value) {
            setRegistryName(value.replace(/[^A-Za-zА-Яа-я0-9\s-_!?=.]+/g, ''));
        }

        function onSelectProject(project) {
            setProjectUUID(project.uuid);
            setSelectedTerminal(null);
            setProviderUUID(null);
            setSelectedProject(project);
            setLoadingTerminals(true);

            if (project.uuid) {
                new Api({token}).getTags(project.uuid).then(
                    (r) => {
                        setSuggestionsTags(r);
                    }
                )
            }

            new Api({token}).getProjectData(project.uuid).then(
                (r) => {
                    // Только терминалы, умеющие выплату на карту. СБП-терминал
                    // здесь показывать нельзя: файл с картами уедет в отказ на
                    // банке получателя, а страница до этого попросит у мерчанта
                    // номера карт там, где через терминал платят по телефону.
                    // Фильтруем ДО проверки «терминал единственный», иначе у
                    // мерчанта с обоими терминалами подставится не тот.
                    const usableTerminals = filterTerminalsByMethod(r.terminals, PAYOUT_METHOD_CARD);

                    if (usableTerminals.length === 1) {
                        const t = usableTerminals[0];

                        setTerminalBalance({balance: t.balance.confirmed, currency: t.currency});
                        setProviderUUID(t.uuid);
                        setSelectedTerminal(t);

                        if (Array.isArray(initRegistry) && initRegistry.length > 0) {
                            var payoutFee = t && t.fee ? t.fee.payout : null;
                            calcCommission(initRegistry, payoutFee);

                        } else {
                            setCommission(0);
                        }
                    } else {
                        setCommission(0);
                    }

                    setTerminals(usableTerminals);
                    setLoadingTerminals(false);
                }
            )
        }

        function onSelectTerminal(terminal) {
            setSelectedTerminal(terminal);
            setProviderUUID(terminal.uuid);
            setTerminalBalance({balance: terminal.balance.confirmed, currency: terminal.currency});

            if (Array.isArray(initRegistry) && initRegistry.length > 0) {
                var payoutFee = terminal && terminal.fee ? terminal.fee.payout : null;
                calcCommission(initRegistry, payoutFee);

            } else {
                setCommission(0);
            }
        }

        function onSelectedTag(tags) {
            setTags(tags);
        }

        function setFilterErrors(val) {

            if (val) {
                const filteredData = initRegistry.filter(function (e) {
                    return e.hasError;
                });

                setFilteredRegistry(filteredData);

            } else {
                setFilteredRegistry([]);
            }
        }

        function submitRegistry() {

            if (!providerUUID || !projectUUID) {
                onMessage({type: 'warning', message: 'Для отправки реестра выберите терминал.'})
                return false;
            }

            setIsRequest(true);

            const registry = [];
            let name;

            initRegistry.forEach(function (item) {
                registry.push(item.row);
            });

            if (registryName) {
                name = registryName;
            } else {
                name = initRegistryName;
            }

            new Api({token}).registryProcess({registry: registry, tags, name, providerUUID}, projectUUID).then(
                function (r) {
                    if (r.result === "ok") {
                        clearRegistry();
                        setIsRequest(false);
                        onMessage({type: "success", message: "Реестр отправлен в очередь выплат."});
                    } else {
                        onMessage({type: "error", message: +('code: ' + r.code)});
                        setLoadingRegistry(false);
                    }
                }
            );
        }

        function clearRegistry() {
            setInitRegistry([]);
            setFilteredRegistry([]);
            setLoadingRegistry(false);
            setRegistryAmount(0);
            setCommission(0);
            setRegistryName('');
            setInitRegistryName('');
        }

        // Process uploaded data (extracted for reuse after duplicate confirmation)
        function processUploadedData(file, data) {
            var cleanName = file.name.replace(/[^A-Za-zА-Яа-я0-9\s-_!?=.]|\.xlsx+/g, '');
            setInitRegistryName(cleanName);
            setRegistryName(cleanName);

            var rows = (data && Array.isArray(data.data)) ? data.data : [];

            // ВАЖНО: применяем требование FinStar сразу
            rows = applyFinStarDocValidation(rows, selectedTerminal ? selectedTerminal.processing : null);

            if (rows.length > 0) {
                var amount = 0;

                rows.forEach(function (el) {
                    var v = el && el.row ? el.row.amount : 0;
                    amount += Number(v) || 0;
                });

                setRegistryAmount(amount);
                setInitRegistry(rows);
                setFilteredRegistry(rows);
                setLoadingRegistry(true);

                var payoutFee = selectedTerminal && selectedTerminal.fee ? selectedTerminal.fee.payout : null;
                calcCommission(rows, payoutFee);
            } else {
                setLoadingRegistry(false);
                setRegistryAmount(0);
                setInitRegistry([]);
                setFilteredRegistry([]);
                setCommission(0);
            }

            if (data && data.errors) {
                if (data.errors.count) {
                    setErrorsCount(data.errors.count);
                    setErrorsList(data.errors);
                } else {
                    setErrorsCount(0);
                }

                if (data.errors.formatError) {
                    setErrorsFormat(true);
                }

                setWrongRegistryType(data.errors.wrongRegistryType || null);
                setWrongRegistryHint(data.errors.wrongRegistryHint || null);
            } else {
                setWrongRegistryType(null);
                setWrongRegistryHint(null);
            }
        }

        function onRegistryUpload(file, data) {
            // Check for duplicates
            if (data && data.duplicates && Array.isArray(data.duplicates) && data.duplicates.length > 0) {
                setDuplicates(data.duplicates);
                setPendingFile(file);
                setPendingData(data);
                setShowDuplicateWarning(true);
                return;
            }

            // No duplicates - process immediately
            processUploadedData(file, data);
        }

        function handleConfirmDuplicate() {
            setShowDuplicateWarning(false);
            if (pendingFile && pendingData) {
                processUploadedData(pendingFile, pendingData);
            }
            setDuplicates([]);
            setPendingFile(null);
            setPendingData(null);
        }

        function handleCancelDuplicate() {
            setShowDuplicateWarning(false);
            setDuplicates([]);
            setPendingFile(null);
            setPendingData(null);
        }

        return (
            <Wrapper>
                <Context.Provider value={{
                    setFilterErrors,
                    onSelectedTag,
                    onChangeRegistryName,
                    submitRegistry,
                    clearRegistry
                }}>
                    <Container className={classes.StackOnMobile}>
                        <Column width="780" className={classes.MainColumn}>
                            {loadingRegistry ?
                                <div className={classes.Card}>
                                    <RegistryTable
                                        data={filteredRegistry.length > 0 ? filteredRegistry : initRegistry}
                                        processing={selectedTerminal ? selectedTerminal.processing : null}
                                    />
                                </div>
                                :
                                <React.Fragment>
                                    <RegistryDADUpload token={token} onRegistryUpload={onRegistryUpload}
                                                       merchantUuid={projectUUID}/>
                                    <WrongRegistryTypeHint
                                        wrongType={wrongRegistryType}
                                        hint={wrongRegistryHint}
                                    />
                                    <RegistryColumnsHelp
                                        token={token}
                                        type="card"
                                        provider={selectedTerminal ? selectedTerminal.processing : null}
                                    />
                                </React.Fragment>
                            }
                        </Column>

                        <Column width="350" className={classes.SideColumn}>
                            <RegistryUploadInfo name={initRegistryName} errorsCount={errorsCount} errorsList={errorsList}
                                                errorsFormat={errorsFormat} balance={terminalBalance}
                                                amount={registryAmount} fee={commission}/>

                            <EmptyCard>
                                <Select
                                    label={"FieldsHelper.label.project"}
                                    placeholder={"FieldsHelper.label.selectProject"}
                                    isLoading={loadingProjects}
                                    values={projects}
                                    onChange={onSelectProject}
                                    selectedValue={selectedProject}
                                    isClean={false}
                                    fieldName={"displayName"}
                                    fieldStatus={"status"}
                                />

                                {selectedProject && (
                                    <RegistryInfoCard
                                        variant="project"
                                        project={selectedProject}
                                        terminals={terminals}
                                    />
                                )}

                                <Select
                                    label={"FieldsHelper.label.terminal"}
                                    placeholder={"FieldsHelper.label.selectTerminal"}
                                    isLoading={loadingProjects || !selectedProject || loadingTerminals}
                                    values={terminals}
                                    onChange={onSelectTerminal}
                                    selectedValue={selectedTerminal}
                                    isClean={false}
                                    fieldName={"name"}
                                    fieldStatus={"enabled"}
                                />

                                {selectedTerminal && (
                                    <RegistryInfoCard
                                        variant="terminal"
                                        terminal={selectedTerminal}
                                        currentMethod="card"
                                    />
                                )}

                                {selectedProject && !loadingTerminals && terminals.length === 0 && (
                                    <NoTerminalsForMethod currentMethod="card"/>
                                )}
                            </EmptyCard>

                            {loadingRegistry && <RegistryControls
                                errorsCount={errorsCount}
                                suggestionsTag={suggestionsTags}
                                registryFileName={registryName}
                                amount={registryAmount}
                                fee={commission}
                                balance={terminalBalance}
                                isLoading={isRequest}
                            />}
                        </Column>
                    </Container>
                </Context.Provider>

                {/* Duplicate Warning Modal */}
                <Modal
                    isOpen={showDuplicateWarning}
                    onClose={handleCancelDuplicate}
                    onConfirm={handleConfirmDuplicate}
                    title={<FormattedMessage
                        id="RegistryUpload.modal.duplicateTitle"
                        defaultMessage="Обнаружен возможный дубликат"
                    />}
                    isConfirm={true}
                >
                    <div className={classes.WarningContent}>
                        <Icon icon="warning" size="48px" color="#ff9800"/>
                        <p className={classes.WarningText}>
                            <FormattedMessage
                                id="RegistryUpload.modal.duplicateWarning"
                                defaultMessage="При загрузке файла были обнаружены похожие реестры за последние 30 дней:"
                            />
                        </p>
                        <ul className={classes.DuplicateList}>
                            {duplicates.map((dup, idx) => (
                                <li key={idx}>
                                    <strong>{dup.name}</strong>
                                    {' - '}{dup.date ? new Date(dup.date.date || dup.date).toLocaleDateString() : ''}
                                    {dup.merchantName && <span> ({dup.merchantName})</span>}
                                    {dup.matchReason && dup.matchReason.length > 0 && (
                                        <span className={classes.MatchReason}>
                                            {' ['}
                                            {dup.matchReason.includes('name') &&
                                                <FormattedMessage id="RegistryUpload.match.name"
                                                                  defaultMessage="имя"/>}
                                            {dup.matchReason.includes('name') && dup.matchReason.includes('dataHash') && ', '}
                                            {dup.matchReason.includes('dataHash') &&
                                                <FormattedMessage id="RegistryUpload.match.data"
                                                                  defaultMessage="данные"/>}
                                            {']'}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <p className={classes.WarningConfirm}>
                            <FormattedMessage
                                id="RegistryUpload.modal.duplicateConfirm"
                                defaultMessage="Вы уверены, что хотите продолжить?"
                            />
                        </p>
                    </div>
                </Modal>
            </Wrapper>
        )
    }
)

export default RegistryUpload;



// WEBPACK FOOTER //
// ./src/PagesOfUser/RegistryUpload/index.js